import { forwardRef, useState, useRef } from "react";

import { Pipette } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  HexColorPicker,
  HexColorInput,
  HexAlphaColorPicker,
} from "react-colorful";
import { Button } from "src/primitives/Button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/primitives/Popover";
import { cn } from "src/shadcn/lib/utils";

import { useEyeDropper } from "./hooks/useEyeDropper";
import { useRecentlyUsedColors } from "./hooks/useRecentlyUsedColors";
import { Palette } from "./Palette";
import { Target } from "./Target";
import { PICKER_HEIGHT } from "./constants";
import type { ColorPickerProps } from "./types";
import { getColor } from "./utils";

const ColorPicker = forwardRef<HTMLDivElement, ColorPickerProps>(
  (
    {
      color = "",
      size = "large",
      onChange,
      dropdownProps,
      showEyeDropper = true,
      showHexValue = false,
      showTransparencyControl = false,
      showPicker = true,
      showHexPicker = true,
      colorPalette,
      showRecentlyUsedColors = showPicker,
      className,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [colorInternal, setColorInternal] = useState(color);
    const [isColorSelected, setIsColorSelected] = useState(false);
    const [open, setOpen] = useState(false);
    const hexColorInputValue = useRef("");
    const { isSupported: eyeDropperSupported, open: openEyeDropper } =
      useEyeDropper();
    const [recentlyUsedColors, setRecentlyUsedColors] = useRecentlyUsedColors();

    const PickerComponent = showTransparencyControl
      ? HexAlphaColorPicker
      : HexColorPicker;

    const colorValue = color ?? colorInternal ?? "";

    const onColorChange = (value: string) => {
      setIsColorSelected(true);

      if (onChange) {
        onChange(getColor(value, showTransparencyControl));
      } else {
        setColorInternal(value);
      }
    };

    const onColorInputChange = (hex: string) => {
      hexColorInputValue.current = hex;
      const expectedLength = showTransparencyControl ? 9 : 7;
      if (hex.length !== expectedLength) return;

      onColorChange(hex);
      hexColorInputValue.current = "";
    };

    const onBlur = () => {
      if (!hexColorInputValue.current) return;

      onColorChange(hexColorInputValue.current);
      hexColorInputValue.current = "";
    };

    const pickColor = async () => {
      try {
        const hex = await openEyeDropper();
        onColorChange(hex);
      } catch {
        // User cancelled or API unavailable
      }
    };

    const onOpenChange = (nextOpen: boolean) => {
      if (!nextOpen && showRecentlyUsedColors && isColorSelected) {
        const newColor = getColor(colorValue, showTransparencyControl);
        const filtered = recentlyUsedColors.filter(
          ({ hex }) => hex !== newColor.hex
        );
        const updated = [newColor, ...filtered];
        if (updated.length > 14) updated.pop();

        setRecentlyUsedColors(updated);
        setIsColorSelected(false);
      }

      setOpen(nextOpen);
    };

    return (
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Target
            color={color}
            colorValue={colorValue}
            showHexValue={showHexValue}
            size={size}
          />
        </PopoverTrigger>
        <PopoverContent
          ref={ref}
          align="start"
          side="bottom"
          className={cn("w-auto p-3", className)}
          {...dropdownProps}
        >
          <div className="flex flex-col gap-3 w-56">
            {showPicker && (
              <>
                {showHexPicker && (
                  <div
                    data-testid="neeto-color-picker-section"
                    style={{
                      ["--react-colorful-height" as string]:
                        PICKER_HEIGHT[size] ?? "140px",
                    }}
                  >
                    <PickerComponent
                      color={colorValue}
                      onChange={onColorChange}
                      style={{
                        width: "100%",
                        height: PICKER_HEIGHT[size] ?? "140px",
                      }}
                    />
                  </div>
                )}
                <div className="flex items-center justify-center gap-2">
                  {showEyeDropper && eyeDropperSupported && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={pickColor}
                      aria-label={t(
                        "neetoatoms.colorPicker.pickColor",
                        "Pick color from screen"
                      )}
                    >
                      <Pipette className="size-4" />
                    </Button>
                  )}
                  <div
                    className="flex-1"
                    data-testid="colorpicker-editable-input"
                  >
                    <HexColorInput
                      prefixed
                      alpha={!!showTransparencyControl}
                      color={colorValue}
                      data-testid="colorpicker-editable-input-textbox"
                      onChange={onColorInputChange}
                      onBlur={onBlur}
                      className="h-8 w-full rounded-lg border border-input bg-transparent px-2.5 text-base outline-none focus-visible:border-primary focus-visible:ring-3 focus-visible:ring-primary/15 md:text-sm"
                    />
                  </div>
                </div>
              </>
            )}
            <div
              data-testid="color-palette"
              className={cn(showPicker && "border-t border-border pt-3")}
            >
              <Palette
                color={color}
                colorList={colorPalette}
                onChange={onColorChange}
              />
            </div>
            {showRecentlyUsedColors && recentlyUsedColors.length > 0 && (
              <div
                className="border-t border-border pt-3"
                data-testid="color-palette-recently-used"
              >
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  {t("neetoatoms.colorPicker.recentlyUsed", "Recently used")}
                </p>
                <Palette
                  colorList={recentlyUsedColors}
                  onChange={onColorChange}
                />
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

ColorPicker.displayName = "ColorPicker";

export { ColorPicker };
